// src/components/ChartCard.jsx
import Chart from "chart.js/auto";
import { useEffect, useRef, useCallback } from "react";

export default function ChartCard({
  title,
  labels = [],
  datasets = [],
  type = "pie",
  options = {},
  className = "",
  height = "var(--chart-height, clamp(220px, 36vh, 420px))", // <— control here
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const moRef = useRef(null);
  const roRef = useRef(null);
  const mediaRef = useRef(null);
  const containerRef = useRef(null);

  // ✅ RAF throttle for ResizeObserver
  const rafResizeRef = useRef(null);

  const getVar = (name, fallback) =>
    (typeof window !== "undefined"
      ? getComputedStyle(document.body).getPropertyValue(name).trim()
      : "") || fallback;

  // ✅ Keep scroll position stable during chart rebuild/resize
  const keepScroll = useCallback((fn) => {
    if (typeof window === "undefined") return fn();
    const y = window.scrollY || 0;
    fn();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: "auto" });
      });
    });
  }, []);

  const safeResize = useCallback(() => {
    if (!chartRef.current) return;
    keepScroll(() => {
      try {
        chartRef.current.resize();
      } catch {}
    });
  }, [keepScroll]);

  const build = useCallback(() => {
    const textColor = getVar("--text-color", "#212529");
    const bgColor = getVar("--card-bg", "#ffffff");
    const fontFamily = getVar("--font-family", "inherit");

    Chart.defaults.color = textColor;
    Chart.defaults.font.family = fontFamily;

    // ✅ destroy inside keepScroll (layout shift safe)
    keepScroll(() => {
      chartRef.current?.destroy();
      chartRef.current = null;

      if (!labels.length || !datasets.length || !canvasRef.current) return;

      const safe = datasets.map((ds) => ({
        ...ds,
        label: ds.label || title || "Dataset",
      }));

      const cfg = {
        type,
        data: { labels, datasets: safe },
        options: {
          responsive: true,
          maintainAspectRatio: false, // <— important
          layout: { padding: 8 },
          plugins: {
            legend: {
              display: true,
              position: "bottom",
              labels: {
                color: textColor,
                font: { family: fontFamily, size: 14, weight: "600" },
              },
            },
            tooltip: {
              backgroundColor: bgColor,
              titleColor: textColor,
              bodyColor: textColor,
              titleFont: { family: fontFamily, weight: "bold" },
              bodyFont: { family: fontFamily },
            },
          },
          ...(type !== "pie" && type !== "doughnut"
            ? {
                scales: {
                  x: { ticks: { color: textColor }, grid: { color: "#6663" } },
                  y: { ticks: { color: textColor }, grid: { color: "#6663" } },
                },
              }
            : {}),
          ...options,
        },
      };

      chartRef.current = new Chart(canvasRef.current.getContext("2d"), cfg);
    });
  }, [datasets, keepScroll, labels, options, title, type]); // ✅ removed height

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    build();

    // Rebuild on body class (your theme switch)
    moRef.current = new MutationObserver((m) => {
      if (m.some((x) => x.attributeName === "class")) build();
    });
    moRef.current.observe(document.body, { attributes: true });

    // Rebuild on OS theme change
    mediaRef.current = window.matchMedia?.("(prefers-color-scheme: dark)");
    mediaRef.current?.addEventListener?.("change", build);

    // ✅ Resize on container size change (throttled + scroll-safe)
    roRef.current = new ResizeObserver(() => {
      if (rafResizeRef.current) return;
      rafResizeRef.current = requestAnimationFrame(() => {
        rafResizeRef.current = null;
        safeResize();
      });
    });
    if (containerRef.current) roRef.current.observe(containerRef.current);

    const onWinResize = () => safeResize();
    window.addEventListener("resize", onWinResize);

    return () => {
      window.removeEventListener("resize", onWinResize);
      if (rafResizeRef.current) cancelAnimationFrame(rafResizeRef.current);
      rafResizeRef.current = null;

      roRef.current?.disconnect();
      moRef.current?.disconnect();
      mediaRef.current?.removeEventListener?.("change", build);

      keepScroll(() => {
        chartRef.current?.destroy();
        chartRef.current = null;
      });
    };
  }, [build, safeResize, keepScroll]); // ✅ keep deps minimal + correct

  return (
    <div className={`chart-card flex-fill ${className}`}>
      {title && <div className="chart-header">{title}</div>}
      <div
        className="chart-body"
        ref={containerRef}
        style={{
          height,
          overflowAnchor: "none", // ✅ helps stop scroll anchoring jump
        }}
      >
        <canvas ref={canvasRef} data-chart="responsive" />
      </div>
    </div>
  );
}
